import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShieldCheck, CheckCircle2, Download, Mail, 
  FileText, ArrowRight, Printer, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const letterId = searchParams.get('letter_id');
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Execute PayPal payment if returning from PayPal
  const executePayment = useCallback(async () => {
    if (!paymentId || !payerId || !letterId) return false;
    
    setPaymentProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/execute`, null, {
        params: { payment_id: paymentId, payer_id: payerId }
      });
      
      if (response.data.success) {
        toast.success("Payment successful!");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Payment execution failed:", error);
      toast.error("Payment processing failed. Please try again.");
      navigate(`/preview/${letterId}`);
      return false;
    } finally {
      setPaymentProcessing(false);
    }
  }, [paymentId, payerId, letterId, navigate]);

  const fetchLetter = useCallback(async () => {
    if (!letterId) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${API}/letters/${letterId}`);
      
      if (!response.data.paid) {
        // If not paid and we have PayPal params, try to execute payment
        if (paymentId && payerId) {
          const success = await executePayment();
          if (success) {
            // Refetch after payment
            const newResponse = await axios.get(`${API}/letters/${letterId}`);
            setLetter(newResponse.data);
            setLoading(false);
            return;
          }
        }
        toast.error("Payment not completed");
        navigate(`/preview/${letterId}`);
        return;
      }
      
      setLetter(response.data);
    } catch (error) {
      console.error("Failed to fetch letter:", error);
      toast.error("Failed to load letter");
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [letterId, navigate, paymentId, payerId, executePayment]);

  useEffect(() => {
    fetchLetter();
  }, [fetchLetter]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/letters/${letterId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Security_Deposit_Demand_Letter.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleEmail = async () => {
    setEmailing(true);
    try {
      await axios.post(`${API}/letters/${letterId}/send-email`);
      toast.success("Email sent! Check your inbox.");
    } catch (error) {
      console.error("Email failed:", error);
      if (error.response?.status === 500) {
        toast.error("Email service not configured. Please download the PDF.");
      } else {
        toast.error("Failed to send email. Please try again.");
      }
    } finally {
      setEmailing(false);
    }
  };

  if (loading || paymentProcessing) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">
            {paymentProcessing ? "Processing payment..." : "Loading your letter..."}
          </p>
        </div>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-600">Letter not found</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const tips = [
    {
      title: "Print & Send Certified Mail",
      description: "Send via USPS Certified Mail with Return Receipt Requested. This creates proof of delivery."
    },
    {
      title: "Keep Copies",
      description: "Save a copy of the letter and the certified mail receipt for your records."
    },
    {
      title: "Wait for Response",
      description: "Give your landlord the 10-day deadline mentioned in the letter before taking further action."
    },
    {
      title: "Next Steps if No Response",
      description: "If they don't respond, you can file in small claims court using this letter as evidence."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <span className="font-serif text-lg font-bold text-slate-900">TenantShield</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6"
          >
            <CheckCircle2 className="h-10 w-10 text-green-600 success-checkmark" />
          </motion.div>
          
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Your Letter is Ready!
          </h1>
          <p className="text-slate-600 max-w-lg mx-auto">
            Your demand letter for <span className="font-semibold">${letter.deposit_amount?.toLocaleString()}</span> has been generated. 
            Download it now and send it to your landlord.
          </p>
        </motion.div>

        {/* Download Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white border border-slate-200 shadow-lg p-8 mb-8"
        >
          <div className="flex justify-center">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-pdf-btn"
              className="h-16 text-lg bg-blue-600 hover:bg-blue-700 text-white gap-3"
            >
              {downloading ? (
                <>
                  <div className="spinner" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-6 w-6" />
                  Download PDF
                </>
              )}
            </Button>
            
{/* Download only */}
          </div>

          {/* Letter Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-slate-900">Letter Details</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">To:</span>
                <span className="ml-2 text-slate-900 font-medium">{letter.landlord_name}</span>
              </div>
              <div>
                <span className="text-slate-500">From:</span>
                <span className="ml-2 text-slate-900 font-medium">{letter.tenant_name}</span>
              </div>
              <div>
                <span className="text-slate-500">State:</span>
                <span className="ml-2 text-slate-900 font-medium">{letter.state}</span>
              </div>
              <div>
                <span className="text-slate-500">Deposit:</span>
                <span className="ml-2 text-slate-900 font-medium font-mono">
                  ${letter.deposit_amount?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Printer className="h-5 w-5 text-blue-600" />
            <h2 className="font-serif text-xl font-bold text-slate-900">
              What to Do Next
            </h2>
          </div>

          <div className="space-y-4">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                className="bg-white border border-slate-200 p-4 flex gap-4"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                  <p className="text-sm text-slate-600">{tip.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Create Another */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-500 mb-4">Need another letter?</p>
          <Button
            onClick={() => navigate('/create')}
            variant="outline"
            data-testid="create-another-btn"
            className="gap-2"
          >
            Create Another Letter <ArrowRight className="h-4 w-4" />
          </Button>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-blue-400" />
            <span className="font-serif font-bold">TenantShield</span>
          </div>
          <p className="text-slate-400 text-sm">
            Good luck getting your deposit back!
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SuccessPage;
